import React, { Component, createRef } from 'react'
import classnames from 'classnames'
import capitalize from 'lodash/capitalize'

export interface Props {
  title: string
}

export class Collapse extends Component<Props> {
  static displayName = 'Collapse'

  state = {
    open: false,
    disabled: true,
    contentHeight: 0,
  }

  private contentWrapper = createRef<HTMLDivElement>()
  private contentInner = createRef<HTMLDivElement>()
  private resizeObserver: ResizeObserver | null = null

  componentDidMount() {
    const count = React.Children.count(this.props.children)
    const height = this.getHeight()
    if (count > 0) {
      //eslint-disable-next-line
      this.setState({
        contentHeight: height,
        open: true,
        disabled: false,
      })
    }

    // Set up ResizeObserver to watch the inner content div
    if (this.contentInner.current) {
      this.resizeObserver = new ResizeObserver((entries) => {
        // Only update if collapse is open
        if (this.state.open) {
          // Use scrollHeight for accurate measurement
          const newHeight = this.getHeight()
          if (newHeight && newHeight !== this.state.contentHeight) {
            this.setState({ contentHeight: newHeight })
          }
        }
      })
      this.resizeObserver.observe(this.contentInner.current)
    }
  }

  componentDidUpdate(prevProps: Props, prevState: typeof this.state) {
    // If we just opened, recalculate height
    if (!prevState.open && this.state.open) {
      const height = this.getHeight()
      if (height !== this.state.contentHeight) {
        this.setState({ contentHeight: height })
      }
    }
  }

  componentWillUnmount() {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  onClick = () => {
    const { open, disabled } = this.state
    const height = this.getHeight()
    if (open && !disabled) {
      this.setState({
        open: false,
        contentHeight: 0,
      })
    } else if (!disabled) {
      this.setState({
        open: true,
        contentHeight: height,
      })
    }
  }

  getHeight = () => {
    const contentWrapper = this.contentWrapper.current
    return contentWrapper && contentWrapper.scrollHeight
  }

  render() {
    const { title, children } = this.props

    const wrapperClasses = classnames('collapse__wrapper', {
      'collapse__wrapper--open': this.state.open,
    })

    const wrapperStyles = {
      height: this.state.contentHeight || 0,
    }

    const buttonClasses = classnames('collapse__button', {
      'collapse__button--open': this.state.open,
      'collapse__button--disabled': this.state.disabled,
    })

    const headerClasses = classnames('collapse__header', {
      'collapse__header--disabled': this.state.disabled,
    })

    return (
      <div className="collapse">
        <button className={buttonClasses}>Toggle</button>
        <div
          className={headerClasses}
          onClick={this.onClick}
          data-testid="collapseToggle"
        >
          {capitalize(title || '')}
        </div>
        <div
          className={wrapperClasses}
          style={wrapperStyles}
          ref={this.contentWrapper}
        >
          <div className="collapse__content" ref={this.contentInner}>
            {children}
          </div>
        </div>
      </div>
    )
  }
}
